'use strict';

/**
 * Cookie value serializer — RFC 6265-compliant encode/decode utilities.
 *
 * ## Background
 * RFC 6265 (https://www.rfc-editor.org/rfc/rfc6265) defines the allowed character
 * set for cookie values as `cookie-octet`:
 *
 *   cookie-octet = %x21 / %x23-2B / %x2D-3A / %x3C-5B / %x5D-7E
 *
 * Characters explicitly forbidden include:
 *   - %x20  SPACE
 *   - %x22  double-quote "
 *   - %x2C  comma ,
 *   - %x3B  semicolon ;
 *   - %x5C  backslash \
 *
 * ## Problem (Salesforce B2C Commerce 26.5)
 * Starting in version 26.5 the platform's `dw/web/Cookie` class enforces RFC 6265
 * strictly when setting cookies via `response.addHttpCookie()`. Any value that
 * contains a forbidden character (e.g. a JSON string with commas, spaces or
 * double-quotes) is silently dropped — the cookie is never written to the
 * Set-Cookie response header.
 *
 * Prior to 26.5 the check was lenient, so existing cookies stored raw JSON
 * (e.g. `{"products":[],"quantity":0}`) without issues. After the upgrade those
 * cookies stop being set in CI2/sandbox environments (which run 26.5) while
 * Staging and Production (still on 26.4) continue to work.
 *
 * ## Solution
 * Percent-encode the value before storing it (`encodeURIComponent`) and decode it
 * after reading (`decodeURIComponent`). The `%` character (%x25) is a valid
 * cookie-octet, so percent-encoded strings pass RFC 6265 validation.
 *
 * This module provides a thin abstraction so the encoding strategy can be swapped
 * in one place if needed (e.g. migrating to @swaggerexpert/cookie or similar).
 */

/**
 * Encodes a cookie value so it only contains RFC 6265-allowed characters.
 * @param {string} value - Raw cookie value (may contain commas, quotes, spaces, etc.)
 * @returns {string} Percent-encoded string safe to use as a cookie value.
 */
function encodeCookieValue(value) {
    return encodeURIComponent(String(value));
}

/**
 * Decodes a cookie value previously encoded with {@link encodeCookieValue}.
 * @param {string} value - Percent-encoded cookie value as read from the request.
 * @returns {string} Original decoded string.
 */
function decodeCookieValue(value) {
    return decodeURIComponent(value);
}

/**
 * Restores a pre-26.5 OMS cookie value that was stored with single-quotes
 * instead of double-quotes (result of JSON.stringify + replace(/"/g, "'")),
 * making it parseable by JSON.parse.
 * @param {string} value - Single-quoted JSON string.
 * @returns {string} Double-quoted JSON string ready for JSON.parse.
 */
function decodeLegacySingleQuotedJson(value) {
    return value.replace(/'/g, '"');
}

/**
 * Ad-hoc encode/decode pairs for cookies that require custom serialization logic
 * beyond the general RFC 6265 percent-encoding above.
 * Each key is the exact cookie name.
 *
 * Add a new entry here when a cookie has its own value format.
 *
 * Encode always produces RFC 6265-compliant values.
 * Decode is backward-compatible with both legacy and new formats so that
 * cookies already set in user browsers keep working until they expire.
 */
var customCodecs = {
    /**
     * Dispatcher cookie codec.
     * Kept here (rather than in dispatcherHelper) to avoid a circular dependency:
     * dispatcherHelper → countrySelectorHelper → dispatcherHelper.
     *
     * encode: 'country_language' — underscore is RFC 6265-compliant.
     * decode: backward-compatible with the legacy 'country,language' format so
     *         cookies already in user browsers work until they expire naturally.
     */
    dispatcher: {
        /**
         * @param {string} country - 2-char country code (e.g. 'es').
         * @param {string} language - 2-char language code (e.g. 'en').
         * @returns {string} 'country_language'
         */
        encode: function (country, language) {
            return country + '_' + language;
        },
        /**
         * @param {string} value - Raw dispatcher cookie value.
         * @returns {string[]} [country, language]
         */
        decode: function (value) {
            var isRfc6265Format = value.indexOf(',') === -1;
            var separator = isRfc6265Format ? '_' : ',';
            return value.split(separator);
        },
    },

    /**
     * IBMAvailabilityData cookie codec.
     * encode: percent-encodes the JSON string.
     * decode: backward-compatible — pre-26.5 values used single-quotes instead of
     *         double-quotes (first char '{'), so the fallback restores valid JSON.
     */
    IBMAvailabilityData: {
        /**
         * @param {string} jsonString - JSON to store.
         * @returns {string} Percent-encoded JSON string.
         */
        encode: function (jsonString) {
            return encodeCookieValue(jsonString);
        },
        /**
         * @param {string} value - Raw cookie value.
         * @returns {string} JSON string ready for JSON.parse.
         */
        decode: function (value) {
            var isRfc6265Format = value.charAt(0) !== '{';
            return isRfc6265Format
                ? decodeCookieValue(value)
                : decodeLegacySingleQuotedJson(value);
        },
    },

    /**
     * OMSStoresSkuStock cookie codec.
     * Same encode/decode strategy as IBMAvailabilityData.
     */
    OMSStoresSkuStock: {
        /**
         * @param {string} jsonString - JSON to store.
         * @returns {string} Percent-encoded JSON string.
         */
        encode: function (jsonString) {
            return encodeCookieValue(jsonString);
        },
        /**
         * @param {string} value - Raw cookie value.
         * @returns {string} JSON string ready for JSON.parse.
         */
        decode: function (value) {
            var isRfc6265Format = value.charAt(0) !== '{';
            return isRfc6265Format
                ? decodeCookieValue(value)
                : decodeLegacySingleQuotedJson(value);
        },
    },
};

module.exports = {
    encodeCookieValue: encodeCookieValue,
    decodeCookieValue: decodeCookieValue,
    customCodecs: customCodecs,
};
